import React, { useState, useEffect, useCallback } from 'react';
import { useSocket } from '../context/SocketContext';
import axiosInstance from '../api/axios';
import PresetPreview from '../components/PresetPreview';
import './OBSPresets.css';

// ─────────────────────────────────────────────
// OBSConnectionBanner
// ─────────────────────────────────────────────
function OBSConnectionBanner() {
  return (
    <div className="obs-connection-banner">
      <span className="obs-connection-banner__icon">⚠️</span>
      <span>OBS ist nicht verbunden. Bitte verbinde OBS in den Einstellungen.</span>
    </div>
  );
}

// ─────────────────────────────────────────────
// PresetCard
// ─────────────────────────────────────────────
function PresetCard({ preset, isActive, isLoading, obsConnected, onActivate }) {
  return (
    <button
      className={`preset-card ${isActive ? 'preset-card--active' : ''} ${isLoading ? 'preset-card--loading' : ''}`}
      onClick={() => onActivate(preset)}
      disabled={!obsConnected || isLoading}
      title={preset.name}
    >
      <div className="preset-card__preview">
        <PresetPreview preset={preset} />
        {isLoading && (
          <div className="preset-card__spinner-overlay">
            <div className="preset-card__spinner" />
          </div>
        )}
        {isActive && !isLoading && (
          <div className="preset-card__active-badge">✓</div>
        )}
      </div>
      <div className="preset-card__name">{preset.name}</div>
    </button>
  );
}

// ─────────────────────────────────────────────
// PresetGroup
// ─────────────────────────────────────────────
function PresetGroup({ title, presets, activePresetName, loadingPresetId, obsConnected, onActivate }) {
  if (!presets || presets.length === 0) return null;

  return (
    <section className="preset-group">
      <h2 className="preset-group__title">{title}</h2>
      <div className="preset-group__grid">
        {presets.map((preset) => (
          <PresetCard
            key={preset.id}
            preset={preset}
            isActive={activePresetName === preset.name}
            isLoading={loadingPresetId === preset.id}
            obsConnected={obsConnected}
            onActivate={onActivate}
          />
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────
// ScenePresetBar
// ─────────────────────────────────────────────
function ScenePresetBar({ presets, activePresetName, loadingPresetId, obsConnected, onActivate }) {
  if (!presets || presets.length === 0) return null;

  const icons = {
    'Pause': '⏸️',
    'Einstellungen': '⚙️'
  };

  return (
    <section className="scene-preset-bar">
      <h2 className="scene-preset-bar__title">Szenen</h2>
      <div className="scene-preset-bar__buttons">
        {presets.map((preset) => (
          <button
            key={preset.id}
            className={`scene-preset-btn ${activePresetName === preset.name ? 'scene-preset-btn--active' : ''} ${loadingPresetId === preset.id ? 'scene-preset-btn--loading' : ''}`}
            onClick={() => onActivate(preset)}
            disabled={!obsConnected || loadingPresetId === preset.id}
          >
            <span className="scene-preset-btn__icon">{icons[preset.name] || '🎬'}</span>
            <span className="scene-preset-btn__name">{preset.name}</span>
            {loadingPresetId === preset.id && <span className="scene-preset-btn__spinner" />}
          </button>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────
// OBSPresets — Hauptkomponente
// ─────────────────────────────────────────────
export default function OBSPresets() {
  const { socket } = useSocket();

  const [presets, setPresets] = useState([]);
  const [activePresetName, setActivePresetName] = useState(null);
  const [obsConnected, setObsConnected] = useState(false);
  const [loadingPresetId, setLoadingPresetId] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Presets laden
  const loadPresets = useCallback(async () => {
    try {
      const [presetsRes, statusRes] = await Promise.all([
        axiosInstance.get('/api/obs-presets'),
        axiosInstance.get('/api/obs-presets/status')
      ]);
      setPresets(presetsRes.data);
      if (statusRes.data.activePreset) {
        setActivePresetName(statusRes.data.activePreset.name);
      }
      setObsConnected(statusRes.data.obsConnected);
    } catch (err) {
      console.error('[OBSPresets] Fehler beim Laden:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPresets();
  }, [loadPresets]);

  // Socket.IO Events
  useEffect(() => {
    if (!socket) return;

    const onPresetChanged = (data) => {
      setActivePresetName(data.name);
      setLoadingPresetId(null);
    };

    const onPresetDisconnected = () => {
      setObsConnected(false);
      setLoadingPresetId(null);
    };

    const onPresetStatus = (data) => {
      setObsConnected(data.obsConnected);
      if (data.activePreset) {
        setActivePresetName(data.activePreset.name);
      }
    };

    const onPresetCreated = () => loadPresets();
    const onPresetDeleted = () => loadPresets();
    const onPresetUpdated = () => loadPresets();

    socket.on('obs:presetChanged', onPresetChanged);
    socket.on('obs:presetDisconnected', onPresetDisconnected);
    socket.on('obs:presetStatus', onPresetStatus);
    socket.on('obs:presetCreated', onPresetCreated);
    socket.on('obs:presetDeleted', onPresetDeleted);
    socket.on('obs:presetUpdated', onPresetUpdated);

    return () => {
      socket.off('obs:presetChanged', onPresetChanged);
      socket.off('obs:presetDisconnected', onPresetDisconnected);
      socket.off('obs:presetStatus', onPresetStatus);
      socket.off('obs:presetCreated', onPresetCreated);
      socket.off('obs:presetDeleted', onPresetDeleted);
      socket.off('obs:presetUpdated', onPresetUpdated);
    };
  }, [socket, loadPresets]);

  // Preset aktivieren
  const handleActivate = useCallback(async (preset) => {
    if (!obsConnected || loadingPresetId) return;

    setLoadingPresetId(preset.id);
    setError(null);

    try {
      await axiosInstance.post(`/api/obs-presets/${preset.id}/activate`);
      // Erfolg wird über Socket-Event obs:presetChanged bestätigt
    } catch (err) {
      setLoadingPresetId(null);
      const msg = err.response?.data?.error || err.message || 'Unbekannter Fehler';
      setError(msg);
      // Fehlermeldung nach 5 Sekunden ausblenden
      setTimeout(() => setError(null), 5000);
    }
  }, [obsConnected, loadingPresetId]);

  // Presets nach Typ und Kategorie gruppieren
  const scenePresets = presets.filter((p) => p.type === 'scene');
  const onlyCamPresets = presets.filter((p) => p.category === 'only-cam');
  const camInCamPresets = presets.filter((p) => p.category === 'cam-in-cam');
  const camPlusCamPresets = presets.filter((p) => p.category === 'cam-plus-cam');

  if (loading) {
    return (
      <div className="obs-presets obs-presets--loading">
        <div className="obs-presets__spinner" />
        <p>Lade Presets...</p>
      </div>
    );
  }

  return (
    <div className="obs-presets">
      <div className="obs-presets__header">
        <h1 className="obs-presets__title">🎬 OBS Presets</h1>
        <a href="/settings/obs-presets" className="obs-presets__settings-link">
          ⚙️ Einstellungen
        </a>
      </div>

      {!obsConnected && <OBSConnectionBanner />}

      {error && (
        <div className="obs-presets__error">
          <span>❌ {error}</span>
        </div>
      )}

      <ScenePresetBar
        presets={scenePresets}
        activePresetName={activePresetName}
        loadingPresetId={loadingPresetId}
        obsConnected={obsConnected}
        onActivate={handleActivate}
      />

      <div className="obs-presets__divider" />

      <PresetGroup
        title="Only Cam"
        presets={onlyCamPresets}
        activePresetName={activePresetName}
        loadingPresetId={loadingPresetId}
        obsConnected={obsConnected}
        onActivate={handleActivate}
      />

      <PresetGroup
        title="Cam in Cam"
        presets={camInCamPresets}
        activePresetName={activePresetName}
        loadingPresetId={loadingPresetId}
        obsConnected={obsConnected}
        onActivate={handleActivate}
      />

      <PresetGroup
        title="Cam + Cam"
        presets={camPlusCamPresets}
        activePresetName={activePresetName}
        loadingPresetId={loadingPresetId}
        obsConnected={obsConnected}
        onActivate={handleActivate}
      />
    </div>
  );
}
